import { useEffect, useRef, useState } from "react";
import {
  assessResearch, discoverResearch, fetchResearchSources,
  type ResearchAssessment, type ResearchDiscovery, type ResearchSource
} from "../../api/research-client";
import "./creator-research.css";

const statusLabels: Record<ResearchAssessment["status"], string> = {
  needs_review: "Necesita revisión",
  needs_testing: "Pendiente de pruebas",
  not_recommended: "No recomendado para nuevas integraciones"
};

export function CreatorResearchSection({ sessionToken }: { sessionToken: string }) {
  const [opened, setOpened] = useState(false);
  return (
    <details className="account-card creator-research" onToggle={(event) => {
      if (event.currentTarget.open) setOpened(true);
    }}>
      <summary>Investigación y mejoras</summary>
      {opened && <CreatorResearchPanel sessionToken={sessionToken} />}
    </details>
  );
}

function CreatorResearchPanel({ sessionToken }: { sessionToken: string }) {
  const [sources, setSources] = useState<ResearchSource[]>([]);
  const [sourceId, setSourceId] = useState("computer-vision");
  const [discovery, setDiscovery] = useState<ResearchDiscovery>();
  const [assessments, setAssessments] = useState<Record<string, ResearchAssessment>>({});
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy("sources");
    setError("");
    setSources([]);
    setDiscovery(undefined);
    setAssessments({});
    fetchResearchSources(sessionToken, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (result.schemaVersion !== 1 || !Array.isArray(result.sources)) {
          throw new Error("La información del servidor tiene un formato incompatible.");
        }
        setSources(result.sources);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "No se pudieron cargar las fuentes.");
      })
      .finally(() => { if (!controller.signal.aborted) setBusy(""); });
    return () => { controller.abort(); requestRef.current?.abort(); };
  }, [sessionToken, attempt]);

  function changeSource(value: string) {
    requestRef.current?.abort();
    requestRef.current = null;
    setSourceId(value);
    setDiscovery(undefined);
    setAssessments({});
    setQuery("");
    setVisibleCount(20);
    setBusy("");
    setError("");
  }

  async function discover() {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy("discover");
    setError("");
    try {
      const result = await discoverResearch(sessionToken, sourceId, controller.signal);
      if (controller.signal.aborted) return;
      if (result.sourceId !== sourceId || !Array.isArray(result.candidates)) {
        throw new Error("No se pudo verificar el catálogo recibido.");
      }
      setDiscovery(result);
      setAssessments({});
      setVisibleCount(20);
    } catch (reason) {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "No se pudo consultar la fuente.");
    } finally {
      if (!controller.signal.aborted) setBusy("");
    }
  }

  async function assess(repository: string) {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(repository);
    setError("");
    try {
      const result = await assessResearch(sessionToken, sourceId, repository, controller.signal);
      if (controller.signal.aborted) return;
      if (result.repository !== repository || result.sourceId !== sourceId ||
          result.sourceBlobSha !== discovery?.sourceBlobSha) {
        throw new Error("La fuente ha cambiado. Consulta de nuevo el catálogo antes de evaluar.");
      }
      setAssessments((previous) => ({ ...previous, [repository]: result }));
    } catch (reason) {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "No se pudo evaluar el candidato.");
    } finally {
      if (!controller.signal.aborted) setBusy("");
    }
  }

  function download() {
    if (!discovery) return;
    const report = {
      schemaVersion: 1,
      title: "OMNISYNIA — Investigación y mejoras",
      exportedAt: new Date().toISOString(),
      scope: "Descubrimiento y revisión de metadatos. No acredita pruebas funcionales ni integración.",
      discovery,
      assessments: Object.values(assessments)
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "OMNISYNIA_Investigacion_" + sourceId + ".json";
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  const normalized = query.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const matching = discovery?.candidates.filter((candidate) =>
    (candidate.repository + " " + candidate.title + " " + candidate.description)
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(normalized)
  ) ?? [];

  return (
    <div className="research-content">
      <p>Encuentra herramientas que puedan mejorar OMNISYNIA y prepara su evaluación.</p>
      <p className="research-note">Las listas ayudan a descubrir candidatos. Cada herramienta necesita revisión y pruebas antes de incorporarse.</p>
      <div className="research-controls">
        <label>Área de investigación
          <select value={sourceId} disabled={!sources.length} onChange={(event) => changeSource(event.target.value)}>
            {sources.map((source) => <option key={source.id} value={source.id}>{source.title}</option>)}
          </select>
        </label>
        <button className="secondary-button" type="button" disabled={Boolean(busy) || !sources.length} onClick={() => void discover()}>
          {busy === "discover" ? "Consultando…" : "Buscar herramientas"}
        </button>
      </div>
      {busy === "sources" && <p role="status">Cargando fuentes…</p>}
      {error && <div role="alert"><p>{error}</p>{!sources.length &&
        <button type="button" className="secondary-button" onClick={() => setAttempt((value) => value + 1)}>Reintentar</button>}
      </div>}
      {discovery && <>
        <div className="research-report-header">
          <a href={discovery.sourceUrl} target="_blank" rel="noopener noreferrer">Consultar fuente original</a>
          <button className="secondary-button" type="button" onClick={download}>Descargar informe</button>
        </div>
        <p className="research-note">Fuente consultada: {new Date(discovery.fetchedAt).toLocaleString("es-ES")}. Las consultas se reutilizan durante diez minutos.</p>
        <label className="research-search">Buscar candidatos
          <input type="search" value={query} onChange={(event) => { setQuery(event.target.value); setVisibleCount(20); }} />
        </label>
        <p aria-live="polite">{matching.length} candidatos encontrados · {Object.keys(assessments).length} revisiones de metadatos</p>
        {discovery.truncated && <p>Se muestran los primeros 500 repositorios. Consulta la fuente original para ver los demás.</p>}
        <div className="research-candidates">
          {matching.slice(0, visibleCount).map((candidate) => {
            const result = assessments[candidate.repository];
            return (
              <article className="research-candidate" key={candidate.repository}>
                <h4>{candidate.title}</h4>
                <a href={candidate.url} target="_blank" rel="noopener noreferrer">{candidate.repository}</a>
                {candidate.description && <p>{candidate.description}</p>}
                <button className="secondary-button" type="button" disabled={Boolean(busy)} onClick={() => void assess(candidate.repository)}>
                  {busy === candidate.repository ? "Revisando…" : result ? "Consultar de nuevo" : "Revisar candidato"}
                </button>
                {result && <div className="research-assessment">
                  <strong>{statusLabels[result.status]}</strong>
                  <p>Licencia indicada: {result.license ?? "sin identificar"} · Estrellas: {result.stars.toLocaleString("es-ES")}</p>
                  <p>Último cambio de código: {result.lastPush ? new Date(result.lastPush).toLocaleDateString("es-ES") : "sin fecha verificable"}</p>
                  <ul>{result.signals.map((signal) => <li key={signal}>{signal}</li>)}</ul>
                  <details>
                    <summary>Pruebas necesarias para incorporarlo</summary>
                    <ol>{result.requiredChecks.map((check) => <li key={check}>{check}</li>)}</ol>
                  </details>
                </div>}
              </article>
            );
          })}
        </div>
        {!matching.length && <p>No hay candidatos que coincidan con la búsqueda.</p>}
        {matching.length > visibleCount && <button type="button" className="secondary-button" onClick={() => setVisibleCount((count) => count + 20)}>Ver más candidatos</button>}
        <p className="research-note">Descarga el informe para conservar los resultados antes de salir de esta sección. Ningún candidato se instala ni modifica la memoria de los usuarios desde aquí.</p>
      </>}
    </div>
  );
}
